import React, { FC, FormEvent, useCallback, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../api';
import FlagValueInput from './FlagValueInput';
import PlatformStatsFeatureFlagRules from './PlatformStatsFeatureFlagRules';
import PlatformStatsFeatureFlagHistory from './PlatformStatsFeatureFlagHistory';
import {
  BUCKET_LABELS,
  FlagBucketKey,
  FlagKind,
  FlagRuleScope,
  FlagSummary,
  JsonValue,
  apiErrorMessage,
  archiveImpact,
  flagVariants,
  formatDate,
  formatJson,
  inputToValue,
  valueToInput,
} from './platformStatsFeatureFlagTypes';

interface Props {
  flagKey: string;
  scopes: FlagRuleScope[];
  bucketKeys: FlagBucketKey[];
  /** Bumped by the parent whenever the list is (re)loaded; re-fetches detail and history. */
  version: number;
  onChanged: () => void;
  onClose: () => void;
}

interface StatePatch {
  defaultValue?: JsonValue;
  killSwitch?: boolean;
  rolloutPercent?: number | null;
  rolloutBucketBy?: FlagBucketKey;
  rolloutSalt?: string;
  rolloutValue?: JsonValue;
  reason: string;
}

const FLAG_KINDS: readonly FlagKind[] = [
  'boolean',
  'string',
  'number',
  'json',
  'variant',
];

function asKind(kind: string | undefined): FlagKind {
  return (FLAG_KINDS as readonly string[]).includes(kind ?? '')
    ? (kind as FlagKind)
    : 'json';
}

/**
 * Detail panel for one flag: registry metadata, editable runtime state
 * (default / kill switch / rollout), scoped rules and history.
 */
const PlatformStatsFeatureFlagDetail: FC<Props> = ({
  flagKey,
  scopes,
  bucketKeys,
  version,
  onChanged,
  onClose,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [flag, setFlag] = useState<FlagSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // State form (raw strings; converted on submit)
  const [defaultRaw, setDefaultRaw] = useState('');
  const [killSwitch, setKillSwitch] = useState(false);
  const [rolloutEnabled, setRolloutEnabled] = useState(false);
  const [rolloutPercent, setRolloutPercent] = useState('0');
  const [rolloutBucketBy, setRolloutBucketBy] = useState<FlagBucketKey>('user');
  const [rolloutSalt, setRolloutSalt] = useState('');
  const [rolloutValueRaw, setRolloutValueRaw] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [archiveReason, setArchiveReason] = useState('');
  const [archiving, setArchiving] = useState(false);
  const [archiveError, setArchiveError] = useState<string | null>(null);

  const authHeaders = useCallback(
    async () => ({
      headers: { Authorization: `Bearer ${await getAccessTokenSilently()}` },
    }),
    [getAccessTokenSilently],
  );

  const applyFlag = useCallback(
    (f: FlagSummary) => {
      setFlag(f);
      const kind = asKind(f.state?.kind ?? f.definition?.kind);
      const s = f.state;
      setDefaultRaw(
        valueToInput(kind, s ? s.defaultValue : f.definition?.default),
      );
      setKillSwitch(s?.killSwitch ?? false);
      setRolloutEnabled(s?.rolloutPercent != null);
      setRolloutPercent(String(s?.rolloutPercent ?? 0));
      setRolloutBucketBy(
        ((bucketKeys as readonly string[]).includes(s?.rolloutBucketBy ?? '')
          ? s?.rolloutBucketBy
          : (f.definition?.bucketBy ?? 'user')) as FlagBucketKey,
      );
      setRolloutSalt(s?.rolloutSalt ?? '');
      setRolloutValueRaw(valueToInput(kind, s?.rolloutValue ?? undefined));
    },
    [bucketKeys],
  );

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await api.get<FlagSummary>(
        `/platform-stats/feature-flags/${encodeURIComponent(flagKey)}`,
        await authHeaders(),
      );
      applyFlag(res.data);
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to load flag'));
    }
  }, [applyFlag, authHeaders, flagKey]);

  useEffect(() => {
    void load();
  }, [load, version]);

  /**
   * Called after any successful write: apply the returned state immediately and
   * ask the parent to reload the list, which bumps `version` and refreshes
   * detail + history.
   */
  const afterWrite = useCallback(
    (updated: FlagSummary, message: string) => {
      applyFlag(updated);
      setNotice(message);
      setReason('');
      onChanged();
    },
    [applyFlag, onChanged],
  );

  const kind = asKind(flag?.state?.kind ?? flag?.definition?.kind);
  const variants = flag ? flagVariants(flag.definition, flag.state) : [];
  const variantNames = variants.map((v) => v.key);
  const archived = Boolean(flag?.state?.archivedAt);
  const readOnly = archived || !flag?.inRegistry || !flag?.state;
  const impact = flag ? archiveImpact(flag) : [];

  const handleArchive = async (e: FormEvent) => {
    e.preventDefault();
    setArchiveError(null);
    setNotice(null);
    if (!archiveReason.trim()) {
      setArchiveError('A reason is required for every change');
      return;
    }
    setArchiving(true);
    try {
      const res = await api.post<FlagSummary>(
        `/platform-stats/feature-flags/${encodeURIComponent(flagKey)}/archive`,
        { archived: !archived, reason: archiveReason.trim() },
        await authHeaders(),
      );
      setArchiveReason('');
      afterWrite(res.data, archived ? 'Flag unarchived.' : 'Flag archived.');
    } catch (err) {
      setArchiveError(
        apiErrorMessage(
          err,
          archived ? 'Failed to unarchive flag' : 'Failed to archive flag',
        ),
      );
    } finally {
      setArchiving(false);
    }
  };

  const buildPatch = (): StatePatch => {
    if (!reason.trim())
      throw new Error('A reason is required for every change');
    const patch: StatePatch = { reason: reason.trim() };
    patch.defaultValue = inputToValue(kind, defaultRaw);
    patch.killSwitch = killSwitch;
    if (rolloutEnabled) {
      const pct = Number(rolloutPercent);
      if (!Number.isInteger(pct) || pct < 0 || pct > 100) {
        throw new Error('Rollout percent must be an integer between 0 and 100');
      }
      patch.rolloutPercent = pct;
      patch.rolloutBucketBy = rolloutBucketBy;
      patch.rolloutSalt = rolloutSalt;
      patch.rolloutValue =
        rolloutValueRaw === '' ? null : inputToValue(kind, rolloutValueRaw);
    } else {
      patch.rolloutPercent = null;
    }
    return patch;
  };

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setNotice(null);
    let patch: StatePatch;
    try {
      patch = buildPatch();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : String(err));
      return;
    }
    setSaving(true);
    try {
      const res = await api.patch<FlagSummary>(
        `/platform-stats/feature-flags/${encodeURIComponent(flagKey)}`,
        patch,
        await authHeaders(),
      );
      afterWrite(res.data, 'State saved.');
    } catch (err) {
      setFormError(apiErrorMessage(err, 'Failed to save flag state'));
    } finally {
      setSaving(false);
    }
  };

  if (!flag) {
    return (
      <div className='flag-detail'>
        <div className='detail-header'>
          <h3>{flagKey}</h3>
          <button className='ghost-btn' onClick={onClose}>
            Close
          </button>
        </div>
        {error ? (
          <div className='notice error'>{error}</div>
        ) : (
          <div className='loading'>Loading…</div>
        )}
      </div>
    );
  }

  const d = flag.definition;
  const s = flag.state;
  const devinSession = d?.devinSession ?? s?.devinSession ?? null;

  return (
    <div className='flag-detail'>
      <div className='detail-header'>
        <div>
          <h3>
            <code>{flag.key}</code>
            <span className='chip kind'>{kind}</span>
            {archived && <span className='chip status-archived'>archived</span>}
            {!flag.inRegistry && (
              <span className='chip warn'>not declared in code</span>
            )}
          </h3>
          <p className='description'>{d?.description ?? s?.description}</p>
        </div>
        <button className='ghost-btn' onClick={onClose}>
          Close
        </button>
      </div>

      <dl className='meta'>
        <dt>Owner</dt>
        <dd>{d?.owner ?? s?.owner ?? '—'}</dd>
        <dt>Category</dt>
        <dd>{d?.category ?? s?.category ?? '—'}</dd>
        <dt>Registry default</dt>
        <dd className='mono'>{formatJson(d?.default)}</dd>
        <dt>Expires</dt>
        <dd>{formatDate(d?.expiresAt ?? s?.expiresAt)}</dd>
        <dt>Devin session</dt>
        <dd>
          {devinSession ? (
            <a href={devinSession} target='_blank' rel='noreferrer'>
              {devinSession.replace(/^https?:\/\//, '')}
            </a>
          ) : (
            '—'
          )}
        </dd>
        <dt>Last change</dt>
        <dd>
          {s?.updatedBy ? (
            <>
              {formatDate(s.updatedAt)} by {s.updatedBy}
              {s.updatedReason ? ` — “${s.updatedReason}”` : ''}
            </>
          ) : (
            formatDate(s?.updatedAt)
          )}
        </dd>
        {variants.length > 0 && (
          <>
            <dt>Variants</dt>
            <dd className='mono'>
              {variants.map((v) => `${v.key} (${v.weight})`).join(', ')}
            </dd>
          </>
        )}
      </dl>

      {error && <div className='notice error'>{error}</div>}
      {notice && <div className='notice success'>{notice}</div>}
      {readOnly && (
        <div className='notice info'>
          {archived
            ? `Archived ${formatDate(s?.archivedAt)}${
                s?.archivedBy ? ` by ${s.archivedBy}` : ''
              }. Everyone gets the code default (${formatJson(
                d?.default,
              )}); state and rules are kept but read-only until unarchived.${
                flag.inRegistry
                  ? ''
                  : ' The key is no longer declared in lib/flags/registry.ts, so it cannot be unarchived.'
              }`
            : !flag.inRegistry
              ? 'This flag is not declared in lib/flags/registry.ts and cannot be edited.'
              : 'This flag has no database row yet (it is created on server boot).'}
        </div>
      )}

      {s && flag.inRegistry && (
        <form className='archive-form' onSubmit={handleArchive}>
          <div className='reason-row'>
            <label>
              <span>{archived ? 'Unarchive' : 'Archive'}</span>
              <input
                type='text'
                value={archiveReason}
                onChange={(e) => setArchiveReason(e.target.value)}
                placeholder={
                  archived
                    ? 'Why is this flag needed again? (recorded in history)'
                    : 'Why is this flag done? (recorded in history)'
                }
                required
                aria-label={archived ? 'Unarchive reason' : 'Archive reason'}
              />
              <small>
                {archived
                  ? 'Restores the saved state and rules and puts the flag back in the list.'
                  : `Hides the flag from the list and makes everyone get the code default, exactly as if it had been removed from the registry.${
                      impact.length === 0
                        ? ' Nobody’s value changes right now.'
                        : ''
                    } Undo any time.`}
              </small>
            </label>
            <button
              type='submit'
              className={archived ? 'primary-btn' : 'ghost-btn danger'}
              disabled={archiving}
            >
              {archiving
                ? 'Saving…'
                : archived
                  ? 'Unarchive flag'
                  : 'Archive flag'}
            </button>
          </div>
          {!archived && impact.length > 0 && (
            <div className='notice warning' role='alert'>
              <strong>
                Archiving changes what users get right now (takes effect within
                30s, mid-thread users included):
              </strong>
              <ul>
                {impact.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
              If the experiment won, change the registry default in code and
              deploy first, then archive.
            </div>
          )}
          {archiveError && <div className='notice error'>{archiveError}</div>}
        </form>
      )}

      <form className='state-form' onSubmit={handleSave}>
        <h4>Runtime state</h4>
        <div className='field-grid'>
          <label>
            <span>Default value</span>
            <FlagValueInput
              kind={kind}
              value={defaultRaw}
              onChange={setDefaultRaw}
              variants={variantNames}
              disabled={readOnly}
            />
            <small>Returned when no rule or rollout applies.</small>
          </label>
          <label className='checkbox'>
            <input
              type='checkbox'
              checked={killSwitch}
              onChange={(e) => setKillSwitch(e.target.checked)}
              disabled={readOnly}
            />
            <span>Kill switch</span>
            <small>Ignores rules and rollout; everyone gets the default.</small>
          </label>
        </div>

        <fieldset className='rollout'>
          <legend>
            <label className='checkbox'>
              <input
                type='checkbox'
                checked={rolloutEnabled}
                onChange={(e) => setRolloutEnabled(e.target.checked)}
                disabled={readOnly}
              />
              <span>Percentage rollout</span>
            </label>
          </legend>
          <p className='hint'>
            Deterministic: the bucket is a hash of the chosen identity, so the
            same user / company / organisation / thread always gets the same
            answer. Truly random per-evaluation A/B tests are not supported.
          </p>
          <div className='field-grid'>
            <label>
              <span>Percent</span>
              <input
                type='number'
                min={0}
                max={100}
                step={1}
                value={rolloutPercent}
                onChange={(e) => setRolloutPercent(e.target.value)}
                disabled={readOnly || !rolloutEnabled}
              />
            </label>
            <label>
              <span>Bucket by</span>
              <select
                value={rolloutBucketBy}
                onChange={(e) =>
                  setRolloutBucketBy(e.target.value as FlagBucketKey)
                }
                disabled={readOnly || !rolloutEnabled}
              >
                {bucketKeys.map((k) => (
                  <option key={k} value={k}>
                    {BUCKET_LABELS[k] ?? k}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>Salt</span>
              <input
                type='text'
                value={rolloutSalt}
                onChange={(e) => setRolloutSalt(e.target.value)}
                disabled={readOnly || !rolloutEnabled}
                placeholder='change to reshuffle buckets'
              />
            </label>
            {kind !== 'variant' && (
              <label>
                <span>Value inside rollout</span>
                <FlagValueInput
                  kind={kind}
                  value={rolloutValueRaw}
                  onChange={setRolloutValueRaw}
                  allowEmpty
                  disabled={readOnly || !rolloutEnabled}
                />
                <small>
                  {kind === 'boolean'
                    ? 'Unset means true.'
                    : 'Unset means the default value applies inside the rollout too.'}
                </small>
              </label>
            )}
          </div>
        </fieldset>

        <div className='reason-row'>
          <label>
            <span>Reason</span>
            <input
              type='text'
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder='Why are you changing this? (recorded in history)'
              disabled={readOnly}
              required
            />
          </label>
          <button
            type='submit'
            className='primary-btn'
            disabled={readOnly || saving}
          >
            {saving ? 'Saving…' : 'Save state'}
          </button>
        </div>
        {formError && <div className='notice error'>{formError}</div>}
      </form>

      <PlatformStatsFeatureFlagRules
        flagKey={flag.key}
        kind={kind}
        rules={flag.rules}
        scopes={scopes}
        variants={variantNames}
        readOnly={readOnly}
        authHeaders={authHeaders}
        onSaved={(updated) => afterWrite(updated, 'Rule saved.')}
        onDeleted={(updated) => afterWrite(updated, 'Rule removed.')}
      />

      <PlatformStatsFeatureFlagHistory
        flagKey={flag.key}
        version={version}
        authHeaders={authHeaders}
      />
    </div>
  );
};

export default PlatformStatsFeatureFlagDetail;
